import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { BookOpen, CheckSquare, Clock, ArrowRight, User, Link2, ExternalLink, Keyboard, ClipboardCheck } from 'lucide-react';
import { Course } from '../../types/database';
import * as LucideIcons from 'lucide-react';
import { Spinner } from '../ui/Spinner';
import { toast } from '../ui/Toaster';
import { CourseProgressTimeline } from './CourseProgressTimeline';
import { fmtTime } from '../../lib/datetime';
import { AtajosRevitModal } from '../AtajosRevitModal';
import { ProtocoloBimModal } from '../ProtocoloBimModal';
import { getCourseProtocol } from '../../lib/course-protocols';

/**
 * Algunos "links útiles" se interceptan y abren UI dentro del intranet
 * (modal o navegación) en vez de target=_blank a un URL externo. La
 * detección es por título — robusto a cambios de URL en la BD.
 */
const LINK_TITLE_PROTOCOLO   = ['plugins revit', 'protocolo bim'];
const LINK_TITLE_ENTREGABLES = ['listado de entregables', 'entregables'];
const LINK_TITLE_ACC         = ['autodesk construction cloud (acc)', 'autodesk construction cloud', 'acc'];

const matchTitle = (t: string, opts: string[]) =>
  opts.includes(t.trim().toLowerCase());

interface CourseWithProgress extends Course {
  progress: number;
}

interface TodoTask {
  id: string;
  task_id: string;
  completed: boolean;
  completed_at: string | null;
  todo_tasks: {
    title: string;
    description: string | null;
    due_date: string | null;
    created_at: string;
    courses: {
      name: string;
    };
    profiles: {
      full_name: string;
    };
  };
}

interface UpcomingModule {
  id: string;
  name: string;
  start_date: string;
  end_date: string | null;
  zoom_link: string | null;
  course_id: string;
}

interface SharedLink {
  id: string;
  titulo: string;
  descripcion: string | null;
  url: string;
  icono: string;
}

export function AlumnoDashboard() {
  const { profile } = useAuth();
  const { setSelectedCourse, navigateTo } = useNavigation();
  const [courses, setCourses] = useState<CourseWithProgress[]>([]);
  const [todoTasks, setTodoTasks] = useState<TodoTask[]>([]);
  const [upcomingModules, setUpcomingModules] = useState<UpcomingModule[]>([]);
  const [sharedLinks, setSharedLinks] = useState<SharedLink[]>([]);
  const [stats, setStats] = useState({
    avgGrade: 0,
    attendance: 0,
    completedTasks: 0,
    totalTasks: 0,
  });
  const [loading, setLoading] = useState(true);
  const [atajosOpen, setAtajosOpen] = useState(false);
  const [protocoloOpen, setProtocoloOpen] = useState(false);
  // Link ACC del curso activo (override del link de la BD shared_links).
  // Lo cargamos cuando tenemos cursos y lo usamos para reemplazar el href
  // del item "Autodesk Construction Cloud" en Links Útiles.
  const [accOverrideUrl, setAccOverrideUrl] = useState<string | null>(null);
  // El curso activo es el primero de la lista — el que ve por default en
  // el dashboard y para el que se carga el protocolo.
  const activeCourseId = courses[0]?.id ?? null;

  useEffect(() => {
    if (profile?.id) {
      loadData();
    }
  }, [profile?.id]);

  // Cuando hay curso activo, buscamos su protocolo para usar su link_acc
  // como override del link "Autodesk Construction Cloud" de los Links Útiles.
  useEffect(() => {
    if (!activeCourseId) { setAccOverrideUrl(null); return; }
    (async () => {
      const p = await getCourseProtocol(activeCourseId);
      setAccOverrideUrl(p?.link_acc ?? null);
    })();
  }, [activeCourseId]);

  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel('student_todo_tasks')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'todo_task_recipients',
          filter: `student_id=eq.${profile.id}`,
        },
        () => {
          loadTodoTasks();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  const loadData = async () => {
    try {
      const [enrollmentsRes, gradesRes, attendanceRes, evaluationsRes] = await Promise.all([
        supabase
          .from('course_enrollments')
          .select('*, courses!inner(*)')
          .eq('alumno_id', profile!.id)
          .eq('status', 'active')
          .eq('courses.status', 'active'),
        supabase.from('grades').select('grade').eq('alumno_id', profile!.id),
        supabase.from('attendance').select('present, module_id').eq('alumno_id', profile!.id),
        supabase.from('course_evaluations').select('final_average').eq('alumno_id', profile!.id),
      ]);

      const coursesWithProgress = await Promise.all(
        (enrollmentsRes.data || []).map(async (enrollment: any) => {
          const course = enrollment.courses;
          const { data: modules } = await supabase
            .from('modules')
            .select('id, end_date')
            .eq('course_id', course.id);

          const completedModules = modules?.filter(m => m.end_date && new Date(m.end_date) < new Date()).length || 0;
          const totalModules = modules?.length || 1;
          const progress = Math.round((completedModules / totalModules) * 100);

          return { ...course, progress };
        })
      );

      const evaluationAverages = evaluationsRes.data
        ?.map((e: any) => typeof e.final_average === 'string' ? parseFloat(e.final_average) : e.final_average)
        .filter((avg: number) => !isNaN(avg) && avg > 0) || [];

      const avgGrade = evaluationAverages.length > 0
        ? parseFloat((evaluationAverages.reduce((a: number, b: number) => a + b, 0) / evaluationAverages.length).toFixed(1))
        : 0;

      const enrolledCourseIds = (enrollmentsRes.data || []).map((e: any) => e.courses.id);
      const { data: completedModules } = await supabase
        .from('modules')
        .select('id')
        .in('course_id', enrolledCourseIds)
        .lt('end_date', new Date().toISOString());

      const completedModuleIds = completedModules?.map(m => m.id) || [];
      const attendanceRecords = attendanceRes.data?.filter((a: any) =>
        completedModuleIds.includes(a.module_id)
      ) || [];

      const presentCount = attendanceRecords.filter(a => a.present).length;
      const totalAttendance = completedModuleIds.length || 1;
      const attendance = Math.round((presentCount / totalAttendance) * 100);

      await loadTodoTasks();
      await loadUpcomingModules(enrolledCourseIds);
      await loadLinks();

      setCourses(coursesWithProgress);
      setStats({
        avgGrade,
        attendance,
        completedTasks: 0,
        totalTasks: 0,
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTodoTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('todo_task_recipients')
        .select(`
          id,
          task_id,
          completed,
          completed_at,
          todo_tasks(
            title,
            description,
            due_date,
            created_at,
            courses(name),
            profiles(full_name)
          )
        `)
        .eq('student_id', profile!.id)
        .order('completed', { ascending: true });

      if (error) throw error;

      setTodoTasks(data as TodoTask[]);

      const completedCount = data?.filter(t => t.completed).length || 0;
      setStats(prev => ({
        ...prev,
        completedTasks: completedCount,
        totalTasks: data?.length || 0,
      }));
    } catch (error) {
      console.error('Error loading todo tasks:', error);
    }
  };

  const loadUpcomingModules = async (courseIds: string[]) => {
    try {
      if (courseIds.length === 0) {
        setUpcomingModules([]);
        return;
      }

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('modules')
        .select('id, name, start_date, end_date, zoom_link, course_id')
        .in('course_id', courseIds)
        .gte('start_date', now.toISOString())
        .order('start_date', { ascending: true })
        .limit(5);

      if (error) throw error;

      setUpcomingModules(data || []);
    } catch (error) {
      console.error('Error loading upcoming modules:', error);
      setUpcomingModules([]);
    }
  };

  const loadLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('shared_links')
        .select('*')
        .eq('activo', true)
        .order('orden', { ascending: true })
        .limit(5);

      if (error) throw error;
      setSharedLinks(data || []);
    } catch (error) {
      console.error('Error loading links:', error);
      setSharedLinks([]);
    }
  };

  const toggleTaskCompletion = async (recipientId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('todo_task_recipients')
        .update({
          completed: !currentStatus,
          completed_at: !currentStatus ? new Date().toISOString() : null,
        })
        .eq('id', recipientId);

      if (error) throw error;

      await loadTodoTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Error al actualizar la tarea');
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || LucideIcons.Link;
    return Icon;
  };

  // CircularProgress acepta dos modos:
  //   - Modo % (default): pasás `value` (0..100), muestra "{value}%" en el centro
  //     y dibuja el anillo proporcional al valor.
  //   - Modo texto: pasás `displayText` (ej. "KS") + `fullRing` para que el anillo
  //     se vea 100% pintado (porque no representa un %), y muestra ese texto
  //     en lugar del porcentaje.
  // El label de abajo aplica a los dos modos.
  const CircularProgress = ({
    value,
    size = 120,
    strokeWidth = 8,
    label,
    displayText,
    fullRing = false,
  }: {
    value: number;
    size?: number;
    strokeWidth?: number;
    label: string;
    displayText?: string;
    fullRing?: boolean;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const fillPct = fullRing ? 100 : value;
    const offset = circumference - (fillPct / 100) * circumference;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={strokeWidth}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#DDFF00"
            style={{ filter: 'drop-shadow(0 0 6px rgba(221,255,0,0.6))' }}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-2xl font-bold text-white tracking-wide">
            {displayText ?? `${value}%`}
          </span>
          <span className="text-xs text-muted">{label}</span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const overallProgress = courses.length
    ? Math.round(courses.reduce((sum, c: any) => sum + (c.progress || 0), 0) / courses.length)
    : 0;

  return (
    <div className="space-y-8 animate-slide-up p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <div className="relative overflow-hidden rounded-3xl glass-elevated p-8 border-gradient">
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cubus-lime/10 rounded-full blur-3xl" />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* Saludo */}
          <div>
            <div className="chip chip-lime mb-3">
              <span>Panel Estudiante</span>
            </div>
            <h1 className="text-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-2">
              ¡Hola, <span className="text-lime-glow">{profile?.full_name?.split(' ')[0]}</span>!
            </h1>
            <p className="text-white/60 max-w-xl">Continúa con tu aprendizaje y revisa tus próximas actividades.</p>
          </div>

          {/* Línea de tiempo del curso (curso activo) */}
          {courses.length > 0 && (
            <CourseProgressTimeline course={courses[0]} />
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass-surface rounded-xl p-6 border border-white/[0.06] flex flex-col items-center justify-center">
          <CircularProgress value={overallProgress} label="Progreso" />
          <p className="text-muted text-sm mt-4">
            {courses.length <= 1 ? 'Avance del curso' : `Promedio sobre ${courses.length} cursos`}
          </p>
        </div>

        <div className="glass-surface rounded-xl p-6 border border-white/[0.06] flex flex-col items-center justify-center">
          <CircularProgress value={stats.attendance} label="Asistencia" />
          <p className="text-muted text-sm mt-4">Asistencia General</p>
        </div>

        {/* Tercera card: Atajos Revit (KS) — clickeable.
            Reusa la misma estructura visual que las dos anteriores pero envuelta
            en un <button>: cursor pointer, hover con borde lime, glow más
            intenso. El anillo se ve 100% pintado (no es un porcentaje, es un
            indicador). El texto "KS" reemplaza al "%". */}
        <button
          type="button"
          onClick={() => setAtajosOpen(true)}
          aria-label="Abrir atajos de teclado de Revit"
          className="glass-surface rounded-xl p-6 border border-white/[0.06] hover:border-cubus-lime/50 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group hover:shadow-[0_0_24px_rgba(221,255,0,0.18)] focus-lime"
        >
          <CircularProgress
            value={100}
            label="ATAJOS REVIT"
            displayText="KS"
            fullRing
          />
          <p className="text-muted text-sm mt-4 inline-flex items-center gap-2 group-hover:text-cubus-lime transition-colors">
            <Keyboard className="w-4 h-4" />
            Ver tabla de atajos
          </p>
        </button>

        {/* Cuarta card: ENTREGABLES — clickeable, navega a la página /entregables.
            Mismo lenguaje visual que ATAJOS REVIT pero con texto "E" + label
            ENTREGABLES y subtítulo "Gestionar entregable BIM". */}
        <button
          type="button"
          onClick={() => navigateTo('entregables')}
          aria-label="Abrir gestor de Entregables BIM"
          className="glass-surface rounded-xl p-6 border border-white/[0.06] hover:border-cubus-lime/50 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group hover:shadow-[0_0_24px_rgba(221,255,0,0.18)] focus-lime"
        >
          <CircularProgress
            value={100}
            label="ENTREGABLES"
            displayText="E"
            fullRing
          />
          <p className="text-muted text-sm mt-4 inline-flex items-center gap-2 group-hover:text-cubus-lime transition-colors">
            <ClipboardCheck className="w-4 h-4" />
            Gestionar entregable
          </p>
        </button>
      </div>

      {/* Modal pantalla completa con el dashboard de atajos */}
      <AtajosRevitModal open={atajosOpen} onClose={() => setAtajosOpen(false)} />

      {/* Modal del Protocolo BIM (intercepta el link "Plugins Revit"). Le
          pasamos el curso activo para que cargue las fechas y el link ACC. */}
      <ProtocoloBimModal
        open={protocoloOpen}
        onClose={() => setProtocoloOpen(false)}
        courseId={activeCourseId}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="glass-surface rounded-xl p-6 border border-white/[0.06] lg:col-span-1 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Mis Cursos</h3>
            <BookOpen className="w-5 h-5 text-muted" />
          </div>

          {courses.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-faint mx-auto mb-3" />
              <p className="text-muted">No estás inscrito en ningún curso</p>
            </div>
          ) : (
            <div className={`flex flex-col ${courses.length === 1 ? 'flex-1' : 'space-y-4'}`}>
              {courses.map((course) => (
                <div
                  key={course.id}
                  className={`glass-deep rounded-lg overflow-hidden border border-white/[0.08] hover:border-cubus-lime/40 transition-all duration-200 group ${courses.length === 1 ? 'flex-1 flex flex-col' : ''}`}
                >
                  {course.thumbnail_url && (
                    <div className={`w-full bg-gray-800 overflow-hidden ${courses.length === 1 ? 'flex-1' : 'h-40'}`}>
                      <img
                        src={course.thumbnail_url}
                        alt={course.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-white mb-1 group-hover:text-cubus-lime transition-colors">
                          {course.name}
                        </h4>
                        <p className="text-sm text-muted line-clamp-2">{course.description}</p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-muted">Progreso</span>
                        <span className="text-cubus-lime font-medium">{course.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-cubus-lime h-2 rounded-full transition-all duration-500"
                          style={{ width: `${course.progress}%` }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedCourse({ id: course.id, name: course.name })}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime text-cubus-lime hover:text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                    >
                      Ir al Curso
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="glass-surface rounded-xl p-6 border border-white/[0.06] lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Links Útiles</h3>
            <Link2 className="w-5 h-5 text-muted" />
          </div>

          {sharedLinks.length === 0 ? (
            <div className="text-center py-12">
              <Link2 className="w-12 h-12 text-faint mx-auto mb-3" />
              <p className="text-muted">No hay links disponibles</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {sharedLinks.map((link) => {
                  const Icon = getIcon(link.icono);
                  // ── Interceptamos 3 links especiales: ─────────────────
                  // - "Plugins Revit"          → abre ProtocoloBimModal
                  // - "Listado de Entregables" → navega a /entregables
                  // - "Autodesk Construction Cloud" → si el curso tiene un
                  //                                   link_acc en su protocolo,
                  //                                   lo usamos en vez del de la BD.
                  // El resto siguen como <a target=_blank>.
                  const isProtocolo   = matchTitle(link.titulo, LINK_TITLE_PROTOCOLO);
                  const isEntregables = matchTitle(link.titulo, LINK_TITLE_ENTREGABLES);
                  const isAcc         = matchTitle(link.titulo, LINK_TITLE_ACC);
                  // URL final: si es ACC y hay override, lo usamos; sino el de la BD.
                  const effectiveUrl  = isAcc && accOverrideUrl ? accOverrideUrl : link.url;

                  const displayTitle = isProtocolo ? 'Protocolo BIM CUBUS' : link.titulo;
                  const displayDesc  = isProtocolo
                    ? 'Documento del estándar de modelado BIM (versión clara/oscura).'
                    : isEntregables
                      ? 'Gestioná tu entregable BIM dentro de la intranet.'
                      : link.descripcion;

                  const innerContent = (
                    <>
                      <div className="p-2 bg-cubus-lime/10 rounded-lg group-hover:bg-cubus-lime/20 transition-colors">
                        <Icon className="w-5 h-5 text-cubus-lime" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white group-hover:text-cubus-lime transition-colors truncate">
                          {displayTitle}
                        </h4>
                        {displayDesc && (
                          <p className="text-xs text-muted truncate">{displayDesc}</p>
                        )}
                      </div>
                      <ExternalLink className="w-4 h-4 text-subtle group-hover:text-cubus-lime transition-colors flex-shrink-0" />
                    </>
                  );

                  const className = 'flex items-center gap-3 p-4 glass-deep rounded-lg border border-white/[0.08] hover:border-cubus-lime/40 transition-all duration-200 group text-left w-full';

                  if (isProtocolo) {
                    return (
                      <button key={link.id} type="button" onClick={() => setProtocoloOpen(true)} className={className}>
                        {innerContent}
                      </button>
                    );
                  }
                  if (isEntregables) {
                    return (
                      <button key={link.id} type="button" onClick={() => navigateTo('entregables')} className={className}>
                        {innerContent}
                      </button>
                    );
                  }
                  return (
                    <a
                      key={link.id}
                      href={effectiveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                    >
                      {innerContent}
                    </a>
                  );
                })}
              </div>

              <button
                onClick={() => navigateTo('links')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-cubus-lime hover:bg-cubus-lime/10 border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200"
              >
                Ver todos los links
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        <div className="glass-surface rounded-xl p-6 border border-white/[0.06] lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">Próximas Clases</h3>
            <Clock className="w-5 h-5 text-muted" />
          </div>

          {upcomingModules.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-faint mx-auto mb-3" />
              <p className="text-muted">No hay clases próximas programadas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upcomingModules.map((module, index) => {
                const startDate = new Date(module.start_date);
                const endDate = module.end_date ? new Date(module.end_date) : null;
                const monthNames = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
                const isNext = index === 0;
                const courseName = courses.find(c => c.id === module.course_id)?.name ?? '';

                return (
                  <button
                    key={module.id}
                    type="button"
                    onClick={() => setSelectedCourse({ id: module.course_id, name: courseName })}
                    className={`w-full text-left flex gap-4 p-4 glass-deep rounded-lg border transition-all cursor-pointer ${
                      isNext
                        ? 'border-cubus-lime/60 shadow-[0_0_12px_rgba(197,255,74,0.15)] hover:border-cubus-lime hover:shadow-[0_0_18px_rgba(197,255,74,0.25)]'
                        : 'border-white/[0.08] hover:border-cubus-lime/40'
                    }`}
                  >
                    <div className="text-center flex-shrink-0">
                      <div className="text-cubus-lime font-bold text-xl">{startDate.getUTCDate()}</div>
                      <div className="text-subtle text-xs font-medium">{monthNames[startDate.getUTCMonth()]}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-white">{module.name}</h4>
                        {isNext && (
                          <span className="text-[10px] font-bold text-cubus-bg bg-cubus-lime px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0">
                            Próxima
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted">
                        {fmtTime(module.start_date)}
                        {endDate && ` - ${fmtTime(module.end_date)}`}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="glass-surface rounded-xl p-6 border border-white/[0.06]">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-white">TO-DO List</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted">
              {stats.completedTasks}/{stats.totalTasks} completadas
            </span>
            <CheckSquare className="w-5 h-5 text-muted" />
          </div>
        </div>

        {todoTasks.length === 0 ? (
          <div className="text-center py-12">
            <CheckSquare className="w-12 h-12 text-faint mx-auto mb-3" />
            <p className="text-muted">No tienes tareas asignadas</p>
          </div>
        ) : (
          <div className="space-y-3">
            {todoTasks.map((task) => (
              <div
                key={task.id}
                className={`p-4 rounded-lg border transition-all duration-200 ${
                  task.completed
                    ? 'bg-black/30 border-white/[0.06]'
                    : 'glass-deep border-white/[0.08] hover:border-cubus-lime/40'
                }`}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggleTaskCompletion(task.id, task.completed)}
                    className={`mt-0.5 flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                      task.completed
                        ? 'bg-cubus-lime border-cubus-lime'
                        : 'border-white/[0.1] hover:border-cubus-lime/40'
                    }`}
                  >
                    {task.completed && (
                      <svg
                        className="w-4 h-4 text-cubus-bg"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <h4
                      className={`font-medium mb-1 ${
                        task.completed ? 'text-subtle line-through' : 'text-white'
                      }`}
                    >
                      {task.todo_tasks.title}
                    </h4>
                    {task.todo_tasks.description && (
                      <p className="text-sm text-muted mb-2">{task.todo_tasks.description}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-subtle">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {task.todo_tasks.courses.name}
                      </span>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {task.todo_tasks.profiles.full_name}
                      </span>
                      <span>
                        {new Date(task.todo_tasks.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                      {task.todo_tasks.due_date && (
                        <span className={task.completed ? 'text-faint' : 'text-cubus-lime'}>
                          Vence: {new Date(task.todo_tasks.due_date).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      )}
                    </div>
                    {task.completed && task.completed_at && (
                      <div className="mt-2 text-xs text-cubus-lime">
                        Completada: {new Date(task.completed_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
